/* @jsx h */
import { defineComponent, h } from "vue";
import { RouterLink } from "vue-router";
import {
  SchemaForm,
  useLocalization,
  type FormOptions,
  type SchemaFormInstance,
} from "@lemmonhq/uikit";
import type { ZodTypeAny } from "zod";
import { SignupReview } from "./SignupReview";
import type { SignupForm, SignupStep } from "./signupTypes";

type Props = {
  form: SignupForm;
  steps: SignupStep[];
  currentStep: number;
  activeSchema: ZodTypeAny;
  activeOptions: FormOptions;
  countryLabel: string;
  error: string | null;
  submitting: boolean;
  onFormRef: (instance: SchemaFormInstance | null) => void;
  onSubmit: (event: Event) => void;
  onFormChange: (value: SignupForm) => void;
  onBack: () => void;
  onGoToStep: (index: number) => void;
};

export const SignupFormPanel = defineComponent<Props>({
  name: "SignupFormPanel",
  props: [
    "form",
    "steps",
    "currentStep",
    "activeSchema",
    "activeOptions",
    "countryLabel",
    "error",
    "submitting",
    "onFormRef",
    "onSubmit",
    "onFormChange",
    "onBack",
    "onGoToStep",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const step = props.steps[props.currentStep];
      const isFirst = props.currentStep === 0;
      const isLast = props.currentStep === props.steps.length - 1;
      const stepOf = t("signup.page.step_of")
        .replace("{current}", String(props.currentStep + 1))
        .replace("{total}", String(props.steps.length));

      return (
        <div class="flex w-full flex-1 flex-col px-4 pb-6 sm:px-8 lg:px-10 lg:py-8">
          {/* Compact progress for screens without the sidebar */}
          <div class="sticky top-0 z-20 -mx-4 mb-4 bg-base-200/95 px-4 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:hidden">
            <div class="mb-2 flex items-center justify-between text-xs font-medium text-base-content/60">
              <span>{stepOf}</span>
              <span class="text-accent">{step.label}</span>
            </div>
            <div class="flex gap-1.5">
              {props.steps.map((s, index) => (
                <span
                  key={s.id}
                  class={[
                    "h-1 flex-1 rounded-full",
                    index <= props.currentStep ? "bg-primary" : "bg-base-300",
                  ]}
                />
              ))}
            </div>
          </div>

          <header class="mb-5">
            <h2 class="text-2xl font-semibold text-accent">{step.title}</h2>
            <p class="mt-1 text-sm text-base-content/70">{step.lead}</p>
            <p class="mt-3 text-sm text-base-content/60 lg:hidden">
              {t("signup.page.have_account")}{" "}
              <RouterLink to="/login" class="link link-primary font-medium">
                {t("signup.page.login")}
              </RouterLink>
            </p>
          </header>

          <form
            class="rounded-xl border border-base-300 bg-base-100 shadow-sm"
            onSubmit={props.onSubmit}
            novalidate
          >
            <div class="space-y-2 p-5 sm:p-6">
              {props.error ? (
                <div role="alert" class="alert alert-error text-sm">
                  <span class="material-symbols-outlined">error</span>
                  <span>{props.error}</span>
                </div>
              ) : null}

              {step.id === "account" && (
                <SignupReview
                  form={props.form}
                  countryLabel={props.countryLabel}
                  onEditStep={props.onGoToStep}
                />
              )}

              <SchemaForm
                ref={(instance) =>
                  props.onFormRef(instance as SchemaFormInstance | null)
                }
                key={step.id}
                schema={props.activeSchema}
                options={props.activeOptions}
                value={props.form}
                onChange={props.onFormChange}
              />
            </div>

            <footer class="sticky bottom-0 z-20 flex items-center justify-between gap-3 rounded-b-xl border-t border-base-300 bg-base-100 px-5 py-3 shadow-[0_-8px_16px_-12px_hsl(var(--bc)/0.4)] sm:px-6">
              <button
                type="button"
                class={[
                  "btn btn-ghost",
                  isFirst && "max-sm:hidden sm:invisible",
                ]}
                disabled={isFirst || props.submitting}
                onClick={props.onBack}
              >
                <span class="material-symbols-outlined text-lg">
                  arrow_back
                </span>
                {t("signup.page.back")}
              </button>
              <button
                type="submit"
                class="btn btn-primary max-sm:flex-1 sm:min-w-40"
                disabled={props.submitting}
              >
                {props.submitting ? (
                  <span class="loading loading-spinner loading-sm" />
                ) : isLast ? (
                  <>
                    {t("signup.page.create")}
                    <span class="material-symbols-outlined text-lg">
                      rocket_launch
                    </span>
                  </>
                ) : (
                  <>
                    {t("signup.page.continue")}
                    <span class="material-symbols-outlined text-lg">
                      arrow_forward
                    </span>
                  </>
                )}
              </button>
            </footer>
          </form>
        </div>
      );
    };
  },
});
